/**
 * Vault -> LinkedIn Content Engine: die Uebersetzung an EINER Stelle.
 *
 * Die beiden Vokabulare sind hart disjunkt, und ein Fehlgriff ist kein weicher Fehler:
 * `api/routers/linkedin.py:_enum()` wirft 422 bei jedem unbekannten Slug.
 *
 *  - `analysis.hook.type` ist eine FESTE 7er-Aufzaehlung (services/vault_analysis.py)
 *    und laesst sich deshalb zuordnen.
 *  - `analysis.hook.formula` ist freier Mad-Lib-Text und wird NICHT zugeordnet.
 *  - `analysis.tensions[].category` ist ein frei erfundener snake_case-Slug. Auf Prod
 *    entstanden aus 10 Posts bereits 15+ verschiedene (`insider_vs_outsider`,
 *    `trust_vs_verification`, ...) mit NULL Ueberschneidung zu den fuenf kuratierten
 *    Kategorien der Engine. Jede Tabelle waere binnen Tagen wieder unvollstaendig —
 *    darum bleibt die Tension bewusst auf "Auto" und geht nur als KLARTEXT in den Winkel.
 */
import type { VaultItem } from '../types';
import type { VaultSourceMeta } from '../store/linkedinSlice';

/** hook.type -> hook_formula. Gemessen an den 10 fertigen Analysen auf Prod: 7 von 10 treffen. */
export const VAULT_HOOK_TYPE_TO_FORMULA: Record<string, string> = {
  statistic: 'data_stat_drop',
  provocative: 'provocation',
  contrast: 'contrast',
  statement: 'statement',
  // "use second-person ... concrete scenes only" — genau Direktansprache.
  direct_address: 'pain_point_mirror',
};

/**
 * Fehlt der Typ oder ist er unbekannt, ist das Ergebnis `null` — also "Auto".
 * Abwesenheit ist der Entartungsweg, NIE ein durchgereichter Rohwert.
 */
export function mapVaultHookType(type?: string | null): string | null {
  return VAULT_HOOK_TYPE_TO_FORMULA[(type ?? '').trim().toLowerCase()] ?? null;
}

const MAX_TOPIC_ANGLE = 4900;

function cut(value: unknown, limit: number): string {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text) return '';
  return text.length <= limit ? text : `${text.slice(0, limit).trimEnd()}...`;
}

/** Erste Worte jedes Vault-Winkels. Daran erkennt die LinkedIn-Seite beim Verlassen, ob das
 *  Thema-Feld noch der uebernommene Block ist oder schon eigener Text (ADR 0262). */
export const VAULT_WINKEL_ANFANG = 'Inspiration from Content Vault';

const CLOSING = 'Write an original post on this topic. Do not copy the source wording or structure.';

/**
 * Baut den Freitext fuer `topicAngle`. Rein, ohne Store und ohne React — damit einzeln testbar.
 *
 * `analysis` ist UNGEPRUEFTE Modellausgabe: auch bei analysis_status='succeeded' koennen
 * Felder fehlen oder den falschen Typ haben. Darum durchgehend Optional Chaining.
 */
export function buildVaultTopicAngle(item: VaultItem): string {
  const a = item.analysis;
  const source = item.author_name || item.owner_username || 'unknown';
  const platform = item.platform || 'instagram';
  const lines: string[] = [`${VAULT_WINKEL_ANFANG} — ${platform} post by ${source}`, ''];

  const idea = a?.idea;
  // contrast ist ein OBJEKT und novelty eine ZAHL — naiv interpoliert ergaebe das
  // "[object Object]" bzw. eine nackte Ziffer im Prompt. novelty wird weggelassen.
  const pairs: Array<[string, string]> = [
    ['Topic', cut(idea?.topic, 400)],
    ['Angle', cut(idea?.angle, 400)],
    ['Core insight', cut(idea?.seed, 400)],
    ['Common belief', cut(idea?.contrast?.common_belief, 400)],
    ['Reality', cut(idea?.contrast?.reality, 400)],
  ];

  const tensions = Array.isArray(a?.tensions) ? a!.tensions : [];
  const strongest = [...tensions].sort((x, y) => (y?.intensity ?? 0) - (x?.intensity ?? 0))[0];
  if (strongest) {
    // Nur label + explanation. Der freie `category`-Slug taucht bewusst NIRGENDS auf:
    // er luede das Modell ein, sich an ein Vokabular zu haengen, das die Engine nicht kennt.
    const label = cut(strongest.label, 120);
    const why = cut(strongest.explanation, 400);
    const tension = [label, why].filter(Boolean).join(' — ');
    if (tension) pairs.push(['Tension', tension]);
  }

  const hookText = cut(a?.hook?.text, 300);
  if (hookText) pairs.push(['Hook that worked', `"${hookText}"`]);
  pairs.push(['Summary', cut(a?.summary, 600)]);

  // Ohne Analyse (queued/failed/JSON kaputt) der ehrliche Rueckfall auf den rohen Text.
  const hasAnalysis = pairs.some(([, value]) => value);
  if (!hasAnalysis) {
    pairs.push(['Topic', cut(item.title, 200)]);
    pairs.push(['Source excerpt', cut(item.caption, 800)]);
  }

  for (const [label, value] of pairs) {
    if (value) lines.push(`${label}: ${value}`);
  }
  if (item.url) lines.push(`Source: ${item.url}`);
  lines.push('', CLOSING);

  const composed = lines.join('\n');
  if (composed.length <= MAX_TOPIC_ANGLE) return composed;
  // maxLength am Textarea bremst nur das Tippen, nicht das programmatische Schreiben.
  const head = composed.slice(0, MAX_TOPIC_ANGLE);
  return `${head.slice(0, head.lastIndexOf('\n')).trimEnd()}\n\n${CLOSING}`;
}

/** Anzeige-Daten der Quelle fuer den Vorlagen-Layer im Content Engine. */
export function buildVaultSourceMeta(item: VaultItem): VaultSourceMeta {
  return {
    postId: item.post_id,
    author: item.author_name || item.owner_username || 'unknown',
    platform: item.platform || 'instagram',
    viralScore: item.viral_score ?? null,
    url: item.url || null,
    excerpt: cut(item.caption || item.title, 200),
  };
}

/** Braucht dieses Item die Repost-Huerde? Originale nie, egal wie niedrig der Score. */
export function isRepostBlocked(item: VaultItem, minViralScore: number): boolean {
  if (!item.shared_by) return false;
  if (!minViralScore || minViralScore <= 0) return false;
  return (item.viral_score ?? 0) < minViralScore;
}
